/**
 * Whitelabel editorial funnel page — reached from Instagram / paid ads.
 * NO Titan Infra branding anywhere on this page (legal separation).
 * The only outbound CTA leads to /insight/request-information where the visitor
 * can leave their contact details.
 *
 * SEO: <meta name="robots" content="noindex,nofollow"> is injected client-side.
 * Layout: self-contained, no PublicLayout header/footer.
 */
import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ChevronRight } from "lucide-react";

const useNoIndex = () => {
  useEffect(() => {
    const m = document.createElement("meta");
    m.name = "robots";
    m.content = "noindex,nofollow";
    document.head.appendChild(m);
    const prev = document.title;
    document.title = "L’intelligence artificielle a besoin d’infrastructure";
    return () => { document.head.removeChild(m); document.title = prev; };
  }, []);
};

const stat = (v, l) => ({ v, l });

const KEY_STATS = [
  stat("×10", "de croissance des besoins en compute IA d’ici 2030 (McKinsey, IEA)"),
  stat("450 TWh", "d’électricité consommée par les data centers en 2024 — l’équivalent de la France"),
  stat("< 3 %", "des data centers mondiaux sont aujourd’hui optimisés pour l’IA générative"),
];

const SECTIONS = [
  {
    kicker: "Chapitre 01",
    title: "Ce que l’on ne voit pas.",
    p: "Chaque requête envoyée à un modèle d’intelligence artificielle — une simple question, une image générée, un email traduit — déclenche à l’autre bout du monde une cascade de calculs dans des bâtiments industriels que peu de gens ont vus de leurs propres yeux. Ce sont les data centers. Des kilomètres de câbles, des salles réfrigérées à 21 °C, des baies de serveurs qui dissipent la chaleur d’une petite ville. L’infrastructure invisible sur laquelle repose l’ensemble de l’économie numérique.",
  },
  {
    kicker: "Chapitre 02",
    title: "Le mur de compute.",
    p: "L’entraînement de GPT-4 aurait mobilisé environ 25 000 GPU pendant plus de 90 jours. Celui de la prochaine génération de modèles en mobilisera cinq à dix fois plus. Or, la construction d’un data center hyperscale prend en moyenne 3 à 5 ans, et l’accès aux GPU haut de gamme — les Nvidia H100 puis B200 — est aujourd’hui rationné. Le résultat : une pénurie structurelle de capacité de calcul, à laquelle Google, Microsoft, Meta et Amazon répondent en construisant leurs propres campus à raison de 10 milliards de dollars par site.",
  },
  {
    kicker: "Chapitre 03",
    title: "L’énergie, seule vraie limite.",
    p: "Un rack IA consomme jusqu’à 100 kW — dix fois plus qu’un rack traditionnel. À l’échelle d’un campus, cela représente la consommation électrique d’une ville de 100 000 habitants. C’est pourquoi les nouveaux sites se construisent à proximité immédiate de sources d’énergie stables et décarbonées : hydroélectrique en Scandinavie, géothermique en Islande, solaire dans le désert d’Arizona. L’infrastructure IA n’est plus seulement une question de silicium — c’est une question d’électrons.",
  },
  {
    kicker: "Chapitre 04",
    title: "Une nouvelle catégorie d’actif.",
    p: "Pendant longtemps, les data centers ont été des actifs institutionnels, réservés aux fonds d’infrastructure et aux compagnies d’assurance. La demande exponentielle en compute IA change progressivement la donne : de nouveaux modèles apparaissent, permettant à un cercle plus large de contributeurs de participer à la construction et à l’exploitation de ces infrastructures, en accédant à leur capacité de calcul plutôt qu’à leur bâti. C’est cette évolution que nous explorons.",
  },
];

export default function InsightHome() {
  useNoIndex();

  return (
    <div className="min-h-screen bg-stone-50 text-stone-900" data-testid="insight-home">
      {/* Ultra minimal top ribbon — no logo, no brand */}
      <div className="border-b border-stone-200/70 bg-white/60 backdrop-blur">
        <div className="max-w-6xl mx-auto px-6 md:px-10 h-14 flex items-center justify-between">
          <span className="text-[10px] tracking-[0.3em] uppercase text-stone-500">Dossier éditorial · L’infrastructure de l’IA</span>
          <span className="text-[10px] tracking-[0.25em] uppercase text-stone-400 hidden md:inline">Édition 2026</span>
        </div>
      </div>

      {/* Hero video */}
      <section className="relative h-[85vh] min-h-[520px] w-full overflow-hidden bg-stone-900" data-testid="insight-hero">
        <video
          className="absolute inset-0 w-full h-full object-cover"
          src="/videos/insight-hero-v3.mp4"
          autoPlay
          muted
          loop
          playsInline
          poster=""
        />
        <div className="absolute inset-0 bg-gradient-to-b from-stone-900/40 via-stone-900/20 to-stone-900/80" />
        <div className="absolute inset-0 flex flex-col justify-end p-6 md:p-14 max-w-4xl">
          <motion.p
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
            className="text-[10px] md:text-[11px] tracking-[0.35em] uppercase text-amber-300/90 mb-4"
          >
            Ouverture du dossier
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.15 }}
            style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
            className="text-white text-[38px] md:text-[64px] lg:text-[76px] leading-[1.02] font-normal tracking-tight max-w-3xl"
          >
            L’intelligence artificielle a besoin<br />
            <span className="italic text-stone-200">d’un endroit pour vivre.</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.9, delay: 0.4 }}
            className="mt-6 max-w-2xl text-stone-200/90 text-[15px] md:text-[17px] leading-relaxed"
          >
            Enquête sur ces bâtiments industriels que personne ne voit et qui font tourner la moitié du monde. Pourquoi la demande explose, pourquoi la capacité manque, et pourquoi cela intéresse désormais un cercle beaucoup plus large que les fonds d’infrastructure.
          </motion.p>
        </div>
      </section>

      {/* Key stats strip */}
      <section className="border-y border-stone-200 bg-white" data-testid="insight-stats">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-10 grid grid-cols-1 md:grid-cols-3 gap-8">
          {KEY_STATS.map((s, i) => (
            <div key={i} className="flex flex-col">
              <span
                style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
                className="text-4xl md:text-5xl text-stone-900 font-normal tracking-tight"
              >{s.v}</span>
              <span className="mt-3 text-[13px] leading-relaxed text-stone-600 max-w-[240px]">{s.l}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Editorial body */}
      <section className="max-w-3xl mx-auto px-6 md:px-10 py-20 md:py-28" data-testid="insight-body">
        {SECTIONS.map((s, i) => (
          <motion.article
            key={i}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: i * 0.05 }}
            className="mb-16 md:mb-20 last:mb-0"
          >
            <p className="text-[10px] tracking-[0.35em] uppercase text-amber-700/80 mb-3">{s.kicker}</p>
            <h2
              style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
              className="text-[32px] md:text-[44px] leading-[1.1] tracking-tight text-stone-900 mb-6"
            >
              {s.title}
            </h2>
            <p className="text-[17px] md:text-[19px] leading-[1.75] text-stone-700" style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}>
              {s.p}
            </p>
          </motion.article>
        ))}
      </section>

      {/* CTA */}
      <section className="bg-stone-900 text-stone-100" data-testid="insight-cta">
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <p className="text-[10px] tracking-[0.35em] uppercase text-amber-300/80 mb-4">Recevoir le dossier complet</p>
          <h2
            style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
            className="text-[36px] md:text-[54px] leading-[1.05] tracking-tight text-white max-w-3xl"
          >
            Vous souhaitez comprendre comment participer à la construction de cette infrastructure ?
          </h2>
          <p className="mt-6 text-stone-300 text-[16px] md:text-[18px] leading-relaxed max-w-2xl">
            Laissez-nous vos coordonnées. Un membre de notre équipe vous rappellera dans un délai de 48 h ouvrées pour répondre à vos questions et vous transmettre les informations complémentaires.
          </p>
          <div className="mt-10">
            <Link
              to="/insight/request-information"
              data-testid="insight-cta-btn"
              className="group inline-flex items-center gap-3 px-8 py-4 bg-amber-400 hover:bg-amber-300 text-stone-900 rounded-full text-[13px] tracking-[0.15em] uppercase font-medium transition-colors"
            >
              Recevoir plus d’informations
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
          <p className="mt-8 text-stone-500 text-[12px] leading-relaxed max-w-2xl">
            Aucune information n’est demandée sur cette page. Le formulaire suivant collecte uniquement votre nom, votre email et votre téléphone afin de vous rappeler.
          </p>
        </div>
      </section>

      {/* Editorial footer */}
      <footer className="bg-white border-t border-stone-200">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <p className="text-[11px] text-stone-500 leading-relaxed max-w-2xl">
            Contenu à visée strictement éditoriale et pédagogique. Cette page ne constitue ni une offre, ni une sollicitation d’investissement, ni un conseil financier. Elle a pour seul objet la diffusion d’informations générales sur l’infrastructure de l’intelligence artificielle.
          </p>
          <div className="flex items-center gap-4 text-[11px] text-stone-400 tracking-widest uppercase">
            <span>© 2026 — Édition 01</span>
            <Link to="/insight/request-information" className="inline-flex items-center gap-1 text-stone-600 hover:text-stone-900">
              Contact <ChevronRight className="w-3 h-3" />
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
